/**
 * NavBar - Navigation principale avec hamburger menu mobile (audit UX S1-5)
 */

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { GraduationCap, LogIn, LogOut, Loader2, Menu, X, Rocket, User, Settings } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { useLogin } from "../contexts/LoginContext";
import { useAuth } from "../hooks/useAuth";
import { useBeta } from '../hooks/useBeta';
import { isLoggedIn, normalizeEmail } from "../auth/authStateContract";

function NavBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { openLogin } = useLogin();
  const { userEmail, isPro, isAdmin, isLoading, sessionToken } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isAdminPage = location.pathname.startsWith('/admin');

  const handleLoginClick = () => {
    openLogin(location.pathname);
    setMobileOpen(false);
  };

  const handleLogout = async () => {
    setMobileOpen(false);
    try {
      if (sessionToken) {
        const axios = (await import('axios')).default;
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const API = `${BACKEND_URL}/api`;
        try {
          await axios.post(`${API}/auth/logout`, {}, {
            headers: { 'X-Session-Token': sessionToken }
          });
        } catch (error) {
          console.log('[NavBar] Erreur logout API (ignorée):', error);
        }
      }
    } catch (error) {
      console.error('[NavBar] Erreur lors de la déconnexion:', error);
    } finally {
      localStorage.removeItem('lemaitremot_session_token');
      localStorage.removeItem('lemaitremot_user_email');
      localStorage.removeItem('lemaitremot_login_method');
      window.dispatchEvent(new Event('lmm:auth-changed'));
    }
  };

  const beta = useBeta();
  const authState = { sessionToken, userEmail, isPro, isLoading };
  const isLoggedInState = !isLoading && isLoggedIn(authState);

  const navTo = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  // P1.1 — Clarté guidée : 4 entrées max visiteur, 3 entrées connecté.
  // Tout le reste (compte, Pro, admin) vit dans le menu avatar.
  // Refonte « Encre & Papier » : nav minimale. Le logo ramène à l'accueil,
  // le CTA unique « Créer une fiche » remplace l'entrée « Générateur ».
  const navItems = (
    <>
      {isLoggedInState && (
        <Button variant="ghost" size="sm" onClick={() => navTo('/mes-fiches')}
          className={isActive('/mes-fiches') ? 'text-foreground font-semibold' : 'text-muted-foreground'}>
          {t('nav.mySheets')}
        </Button>
      )}
      {!isLoggedInState && (
        // Pendant la bêta, `/pricing` sert la page bêta : aucun prix n'y
        // figure. Garder le libellé « Tarifs » promettrait une grille qui
        // n'existe pas — la même faute que rediriger en silence, à l'envers.
        <Button variant="ghost" size="sm" onClick={() => navTo('/pricing')}
          className={isActive('/pricing') ? 'text-foreground font-semibold' : 'text-muted-foreground'}>
          {beta.active ? 'La bêta' : 'Tarifs'}
        </Button>
      )}
    </>
  );

  const authSection = (
    <>
      {isLoading ? (
        <Button variant="ghost" size="sm" disabled className="opacity-50">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Chargement...
        </Button>
      ) : !isLoggedInState || !normalizeEmail(userEmail) ? (
        /* P1.1: une seule action d'authentification — « Connexion ».
           (l'ancien bouton vert « Essayer gratuitement » doublonnait
           l'entrée de nav « Créer une fiche ») */
        <Button variant="outline" size="sm" onClick={handleLoginClick} className="">
          <LogIn className="h-4 w-4 mr-1.5" />
          Connexion
        </Button>
      ) : (
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button aria-label="Mon compte" className="flex items-center gap-1.5 text-sm bg-gray-100 dark:bg-gray-800 rounded-lg px-2.5 py-1.5 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                <User className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                {/* Le violet Pro, et lui seul, dit « Pro ». L'ambre disait
                    « attention » ailleurs dans le produit. */}
                {/* Pendant la bêta, le badge dit « Bêta Fondateur » : ces comptes
                      n'ont rien payé et n'ont aucun abonnement — leur afficher
                      « Pro » serait faux. */}
                  {isPro && (
                    <Badge variant="default" className="bg-purple-600 text-white">
                      {beta.active ? 'Bêta Fondateur' : 'Pro'}
                    </Badge>
                  )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="font-normal text-xs text-gray-500 truncate max-w-[200px]">
                {normalizeEmail(userEmail)}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* P0.6: Mon compte accessible à tous les connectés (infos + suppression RGPD) */}
              <DropdownMenuItem onClick={() => { navigate('/compte'); setMobileOpen(false); }}>
                <User className="h-4 w-4 mr-2" />
                Mon compte
              </DropdownMenuItem>
              {!isPro && (
                /* P1.1: les Tarifs sortent de la nav des connectés — l'upgrade vit ici */
                <DropdownMenuItem onClick={() => { navigate('/pricing'); setMobileOpen(false); }}>
                  <Rocket className="h-4 w-4 mr-2" />
                  {beta.active ? 'La bêta fondateur' : 'Passer en Pro'}
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  {/* P1.1: l'admin sort de la barre principale.
                      09/09/2026 : ce lien menait à /admin/curriculum, qui
                      redirige vers /admin/chapitres. Le SEUL chemin cliquable
                      vers l'administration déposait donc dans le catalogue
                      détaillé — un écran d'expertise — au lieu de la porte
                      d'entrée. « Aujourd'hui » n'était atteignable qu'en tapant
                      l'adresse : personne ne la voyait, et l'admin paraissait
                      être un empilement d'onglets sans ordre. */}
                  <DropdownMenuItem onClick={() => { navigate('/admin'); setMobileOpen(false); }}>
                    <Settings className="h-4 w-4 mr-2" />
                    Administration
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                <LogOut className="h-4 w-4 mr-2" />
                Se déconnecter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </>
  );

  // P2.3a: la navigation admin vit désormais dans la sidebar AdminLayout

  return (
    <nav className="sticky top-0 z-50 overflow-x-hidden border-b border-border bg-background/95 backdrop-blur-sm [&_button]:min-h-11 [&_button]:min-w-11">
      <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3 max-w-full">
        <div className="flex items-center justify-between gap-2 min-w-0">
          {/* Logo — compact on mobile */}
          <button className="flex items-baseline gap-2 cursor-pointer min-w-0 shrink-0 bg-transparent border-0 p-0" onClick={() => navTo('/')} aria-label="Le Maître Mot — accueil">
            <span aria-hidden="true" className="inline-flex h-6 w-6 sm:h-7 sm:w-7 translate-y-[3px] items-center justify-center rounded-md rounded-bl-sm bg-primary font-serif text-sm font-bold text-primary-foreground">M</span>
            <span className="truncate font-serif text-lg sm:text-xl font-bold tracking-tight text-foreground">{t('nav.appName')}</span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2 min-w-0">
            {navItems}
            {authSection}
            {/* Ce bouton dit « Créer une fiche » : il annonce donc une
                intention de création, comme celui de Mes fiches. L'atelier
                garde la fiche en cours dans le navigateur ; sans cette
                annonce, on retombait dessus en croyant en commencer une neuve,
                et l'enregistrer la dupliquait. C'est l'atelier qui demandera à
                l'utilisateur de trancher, et seulement s'il y a effectivement
                une fiche en cours. */}
            {!isAdminPage && (
              <Button size="sm" onClick={() => navTo('/')} className="ml-1 min-h-11">
                Créer une fiche
              </Button>
            )}
          </div>

          {/* Mobile hamburger — compact, no overflow */}
          <div className="md:hidden flex items-center gap-1 sm:gap-2 min-w-0 shrink-0">
            <div className="flex items-center gap-1 min-w-0 overflow-hidden">
              {authSection}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setMobileOpen(!mobileOpen)} className="shrink-0 p-2"
              aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'} aria-expanded={mobileOpen}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden mt-3 pt-3 border-t flex flex-col gap-1">
            {navItems}
            {/* Même annonce d'intention que sur ordinateur. */}
            <Button size="sm" onClick={() => navTo('/')} className="min-h-11">Créer une fiche</Button>
          </div>
        )}
      </div>
    </nav>
  );
}

export default NavBar;
